import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GuestbookService } from '../guestbook.service';
import { OrganizationService } from '../../organizations/organization.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Property } from '../property';
import { LanguageService } from '../../shared/services/language.service';
import { Organization } from '../../shared/sdk';
import { Decimal } from 'decimal.js';
import * as moment from 'moment';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-report-view',
  templateUrl: './report-view.component.html',
  styleUrls: ['./report-view.component.css'],
})
export class ReportViewComponent implements OnInit, OnDestroy {
  @Input() activeOrg: Organization;
  @Input() properties: Property[];
  @Input() propertyId: string;

  currencyId: string;
  guests: any[];
  isCompanySearchAllowed = true;
  language: string;
  months: string[];
  numExemptTaxes = 0;
  numFullTaxes = 0;
  numGuests = 0;
  numHalfTaxes = 0;
  numNights: number;
  promoTaxHalfValue: number;
  promoTaxTotal = 0;
  promoTaxValue: number;
  property: Property;
  reportForm: FormGroup;
  taxTypes: any[];
  total = 0;
  ttaxHalfValues: number[] = [];
  ttaxTotal = 0;
  ttaxValues: number[] = [];
  years: string[];
  private onDestroy$: Subject<void> = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private guestbookService: GuestbookService,
    private orgService: OrganizationService,
    private fb: FormBuilder,
    private languageService: LanguageService,
  ) {
    this.taxTypes = this.guestbookService.taxTypes;
  }

  ngOnInit(): void {
    this.languageService
      .getCurrent()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((l) => {
        this.language = l;
      });

    this.activeOrg = this.activeOrg;
    this.currencyId = this.orgService.findDefault(
      this.activeOrg,
      'currencyId',
    ).value;

    // Form control
    this.reportForm = this.fb.group({
      propertyId: [
        this.properties[0] ? this.properties[0].id : null,
        Validators.required,
      ],
      year: [moment().get('year'), Validators.required],
      month: [moment().get('month') + 1, Validators.required],
    });

    this.reportForm.controls.propertyId.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.calculateTotals());
    this.reportForm.controls.year.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.calculateTotals());
    this.reportForm.controls.month.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.calculateTotals());

    this.reportForm.controls.propertyId.patchValue(this.propertyId);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  calculateTotals(): void {
    const propertyIdCtrl = this.reportForm.controls.propertyId;
    const monthCtrl = this.reportForm.controls.month;
    const yearCtrl = this.reportForm.controls.year;
    const thisMonth = moment().get('month') + 1;

    const firstYear = 2017;
    const thisYear = moment().get('year');

    this.years = [...Array(thisYear - firstYear + 1).keys()].map((i) =>
      String(i + firstYear),
    );

    if (yearCtrl.value === moment().get('year')) {
      this.months = [...Array(thisMonth).keys()].map((i) => String(i + 1));

      if (thisMonth < monthCtrl.value) {
        monthCtrl.setValue(thisMonth, { emit: false });
      }
    } else {
      this.months = [...Array(12).keys()].map((i) => String(i + 1));
    }

    this.guestbookService
      .getReport(propertyIdCtrl.value, yearCtrl.value, monthCtrl.value)
      .subscribe((report) => {
        this.total = 0;
        this.ttaxTotal = 0;
        this.promoTaxTotal = 0;
        this.ttaxValues = [];
        this.ttaxHalfValues = [];
        this.guests = [];

        for (const ttv of report.ttaxValues) {
          this.ttaxValues.push(ttv.value);
          this.ttaxHalfValues.push(ttv.halfValue);
        }

        this.numGuests = 0;
        this.numNights = 0;
        this.numFullTaxes = 0;
        this.numHalfTaxes = 0;
        this.numExemptTaxes = 0;

        for (const d of report.data) {
          this.numGuests += d.guests.length;
          this.ttaxTotal += d.sum;
          this.promoTaxTotal += d.sumPromo;
          let taxType;

          // Full
          if (d.proc === 1) {
            this.numFullTaxes += d.count;
            taxType = 'full';
          } else if (d.proc === 0.5) {
            // Half
            this.numHalfTaxes += d.count;
            taxType = 'half';
          } else if (d.proc === 0) {
            // Exempt
            this.numExemptTaxes += d.count;
            taxType = 'promo';
          }

          for (const guest of d.guests) {
            this.numNights += guest.stays;
            let ttaxTotal = 0;
            let promoTaxTotal = 0;
            let total = 0;

            switch (taxType) {
              case 'full':
                ttaxTotal = new Decimal(guest.stays)
                  .times(guest.ttaxVal)
                  .toDP(2, Decimal.ROUND_HALF_UP)
                  .toNumber();
                promoTaxTotal = new Decimal(guest.staysPromo)
                  .times(guest.ttaxpromo)
                  .toDP(2, Decimal.ROUND_HALF_UP)
                  .toNumber();
                break;

              case 'half':
                ttaxTotal = new Decimal(guest.stays)
                  .times(guest.ttaxHalfVal)
                  .toDP(2, Decimal.ROUND_HALF_UP)
                  .toNumber();
                promoTaxTotal = new Decimal(guest.staysPromo)
                  .times(guest.ttaxpromohalf)
                  .toDP(2, Decimal.ROUND_HALF_UP)
                  .toNumber();
                break;
            }

            total = new Decimal(ttaxTotal).plus(promoTaxTotal).toNumber();

            const g = Object.assign(
              {
                ttaxTotal,
                promoTaxTotal,
                total,
              },
              guest,
            );

            this.guests.push(g);
          }
        }

        this.guests = this.guests.sort((a, b) => {
          a = moment(a.checkintime);
          b = moment(b.checkintime);

          if (a.isAfter(b)) {
            return -1;
          } else if (a.isSame(b)) {
            return 0;
          } else {
            return 1;
          }
        });

        this.total = this.ttaxTotal + this.promoTaxTotal;
      });
  }

  onExportPdf(): void {
    const propertyId = this.reportForm.controls.propertyId.value;
    const year = this.reportForm.controls.year.value;
    const month = this.reportForm.controls.month.value;
    const data = document.getElementById('report'); // id

    html2canvas(data).then((canvas) => {
      // Few necessary setting options
      const imgWidth = 208;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const heightLeft = imgHeight;

      const contentDataURL = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4'); // A4 size page of PDF
      const position = 0;

      pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight);

      pdf.save(`export_guestbook_${propertyId}_${year}_${month}.pdf`);
    });
  }

  onClose(): void {
    this.activeModal.dismiss();
  }
}
